import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, MapPin, Clock, Users, Check, Play, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REEL_SRC =
  "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/rl4kh7hm_josephgraves27%20adjutments.mp4";

const EVENT_PHOTOS = [
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/hilueztr_sippaint2.jpg",
    alt: "Guest in green dashiki painting at a Bob'Bae Paint n Sip",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/gkn4yw7j_bobbaerose1.jpg",
    alt: "Stadium-side Paint n Sip during a Broncos game",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/tl06kbbu_bobbaesippaint.jpg",
    alt: "Bob'Bae Paint n Sip setup at a Broncos memorabilia venue",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/bkofnidz_Gilmore%20sip-paint.jpg",
    alt: "Studio Paint n Sip — wine, easels, and good company",
  },
];

const THEME_CATEGORIES = [
  { id: "all", label: "All" },
  { id: "figurative", label: "Figurative" },
  { id: "landscape", label: "Landscapes" },
  { id: "floral", label: "Floral" },
  { id: "western", label: "Western" },
  { id: "whimsical", label: "Whimsical" },
  { id: "pop", label: "Pop Culture" },
  { id: "heritage", label: "Heritage" },
  { id: "holiday", label: "Holiday" },
  { id: "duets", label: "Duets" },
  { id: "kids", label: "Kids" },
];

const THEMES = [
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/cpet2lyq_blackdress.JPG",
    title: "Silhouette in Hoops",
    note: "Bold, graphic, beginner-friendly. A Bob'Bae crowd-pleaser.",
    category: "figurative",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/s73f7l77_crown.JPG",
    title: "Crown & Curls",
    note: "Rainbow-haired royalty. A favorite for bachelorettes and birthdays.",
    category: "figurative",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/lut1mobk_Cowboy%20hat.jpeg",
    title: "Quiet Cowboy",
    note: "Saturated orange field, hat above, pearl-snap shirt below. A signature Black Western theme.",
    category: "western",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/hbyamv4p_Fishing%20sunset.jpg",
    title: "Sunset Cast",
    note: "Silhouette and sky — atmospheric, deceptively simple, very giftable.",
    category: "landscape",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/ke4rjihx_Screenshot%202026-05-18%20at%209.45.54%E2%80%AFAM.png",
    title: "Lone Boat",
    note: "Rower against fire-sky — a classic Bob'Bae landscape. Couples' nights and Father's Day favorite.",
    category: "landscape",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/i52xvsqd_Screenshot%202026-05-18%20at%209.47.27%E2%80%AFAM.png",
    title: "Mountain Dusk",
    note: "Pines, peaks, gold sky, blue water. Cinematic and very Colorado.",
    category: "landscape",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/ttxy3k5z_bridge.jpg",
    title: "Autumn Bridge",
    note: "Classic fall landscape. Great for couples' nights and holiday gatherings.",
    category: "landscape",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/e3qrkkqr_flower%20bug.webp",
    title: "Petal & Ladybug",
    note: "Loose, gestural, joyful. A go-to for spring and mother's-day groups.",
    category: "floral",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/3n7tj5li_Screenshot%202026-05-18%20at%209.43.02%E2%80%AFAM.png",
    title: "Field of Daisies",
    note: "Sky and grass and a dozen smiling flowers. The most approachable theme on the menu.",
    category: "floral",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/rhco910k_light.jpg",
    title: "Lantern in Bloom",
    note: "Whimsical and atmospheric — cherry-blossom Van Gogh energy.",
    category: "whimsical",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/3vmb3ihp_bait.jpg",
    title: "Tackle Box",
    note: "Quirky still life — a Bob'Bae original for fishing fans and Father's Day nights.",
    category: "whimsical",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/c4ueql7e_Screenshot%202026-05-18%20at%2010.42.42%E2%80%AFAM.png",
    title: "Wakanda Forever",
    note: "Black Panther in deep purple. A high-impact theme for milestone birthdays and pop-culture nights.",
    category: "pop",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/3fq7av41_Screenshot%202026-05-18%20at%2010.41.19%E2%80%AFAM.png",
    title: "Gotham Stars",
    note: "Batman cowl on a red, star-spangled field. Bold graphic shapes — perfect for hero-themed parties.",
    category: "pop",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/g6jzo0xu_Screenshot%202026-05-18%20at%2010.40.16%E2%80%AFAM.png",
    title: "Spider-Sense",
    note: "Web-slinger pop-art on bright yellow. Fan-favorite for kids' parties, fan groups, and ironic adult birthdays.",
    category: "pop",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/7iablewv_Daddy%20and%20me.jpg",
    title: "Two Hearts",
    note: "A paint-together duet — two canvases that join into one tree. Booked by couples, fathers & kids, and best friends who leave with linked pieces.",
    category: "duets",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/7gdp601u_Dock.jpg",
    title: "Sandy Path",
    note: "Sea grass, rope, and an open horizon. Calming, coastal, and a popular bridal-shower request.",
    category: "landscape",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/eu811sjl_Couples%20Night%20out.jpg",
    title: "Dressed to the Nines",
    note: "Black-tie duet — afro and gold hoops, cap and pinstripes. The signature Bob'Bae date-night canvas.",
    category: "duets",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/l6nkaizq_Divine%209%20.jpg",
    title: "Divine Nine",
    note: "A salute to Black Greek life — the four sister sororities in full color. Founder's Day, line anniversaries, and chapter socials.",
    category: "heritage",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/fvhqbkeh_Michael.jpeg",
    title: "The King of Pop",
    note: "Fedora, glove, the silhouette everyone knows. A pop-culture showstopper for milestone nights and tribute parties.",
    category: "pop",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/73ageai6_Heels.webp",
    title: "Hot Steppin'",
    note: "Pop-art red heels on a yellow field. A bold, fun girls'-night-out canvas.",
    category: "figurative",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/hqku0hy3_Umbrella%20.jpeg",
    title: "Red in the Rain",
    note: "Cinematic and moody — a single bright umbrella against gray rain. For quiet, atmospheric nights.",
    category: "whimsical",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/a5pymxxo_owl.jpg",
    title: "Wise Eyes",
    note: "Pop-color owl portrait. A vibrant statement piece — a favorite for first-time painters who want something striking.",
    category: "whimsical",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/wfj4qbkz_Bubbles.avif",
    title: "Bubble Wishes",
    note: "Playful, light, full of color. A sweet, joyful piece that works for any age — especially gentle Sunday-afternoon groups.",
    category: "whimsical",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/cwg32u9r_snowman.jpeg",
    title: "Cheery Snowman",
    note: "Top hat, snowflakes, bow tie. A family-friendly holiday-party classic.",
    category: "holiday",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/7t5ovf98_Holiday.jpeg",
    title: "Chevy Christmas",
    note: "Vintage red truck, snow-dusted tree on the tailgate. The Bob'Bae holiday-party flagship — Friendsgiving, office parties, December girls' nights.",
    category: "holiday",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/tn7wk2kx_Screenshot%202026-05-18%20at%209.46.31%E2%80%AFAM.png",
    title: "Little Angler",
    note: "Sun hat, overalls, dock. The Kids' Paint n Sip headliner.",
    category: "kids",
    kid: true,
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/xtv1pgzg_IMG_1729.JPG",
    title: "Cookie Monster",
    note: "From the Kids' Paint n Sip line — watercolor, easy steps, big smiles.",
    category: "kids",
    kid: true,
  },
];

const FAQS = [
  {
    q: "I've never painted before. Will I be lost?",
    a: "Not for a second. Every theme is broken down into simple, guided steps — beginners are exactly who Bob'Bae is built for. You'll leave with a finished canvas you'll actually want to hang up.",
  },
  {
    q: "Is the studio insured?",
    a: "Yes — fully insured for studio and off-site events. Certificates of insurance can be provided for corporate and venue partners on request.",
  },
  {
    q: "Are kids welcome?",
    a: "Only at the dedicated Kids' Paint n Sip experiences. Standard Bob'Bae sessions are an 21+ adult atmosphere (with BYOB on the table). For kids' events — birthdays, school groups, family days — reach out through the form and Bob'Bae will scope a kid-friendly version with simpler themes (like the Cookie Monster shown above).",
  },
  {
    q: "What's the deposit & payment policy?",
    a: "A 25% non-refundable deposit secures your date. The remaining balance is due 48 hours before the event. Deposit is applied to the final total.",
  },
  {
    q: "What's the cancellation & reschedule policy?",
    a: "Free reschedule with 7 or more days' notice. Inside 7 days, the deposit is forfeited but applied to a future booking within 6 months. No-shows forfeit the deposit.",
  },
  {
    q: "What does Bob'Bae need from an off-site venue?",
    a: "Tables and seating for your guests, one accessible power outlet, a clear 6×6 ft area for the instructor station, and adequate lighting. Bob'Bae brings everything else — supplies, easels, aprons, sound, character.",
  },
  {
    q: "Will Bob'Bae travel outside Denver metro?",
    a: "By arrangement. Off-site rate covers Denver metro; longer travel (Boulder, Fort Collins, Colorado Springs, etc.) is quoted on a per-event basis based on distance and time.",
  },
  {
    q: "What if a guest doesn't drink?",
    a: "All packages are BYOB and there's never any pressure. Bring sparkling water, mocktails, juice — whatever the room wants. The point is the painting and the people.",
  },
  {
    q: "Do you handle corporate invoicing & insurance docs?",
    a: "Yes. For Corporate-tier bookings we provide a formal invoice, a W-9, and a Certificate of Insurance on request. Net-30 available for established companies; for first-time corporate clients the standard 25% deposit applies.",
  },
  {
    q: "How far in advance should I book?",
    a: "2 weeks minimum lead time. For weekend dates and party-season months (Oct–Dec, Apr–Jun), more like 4–6 weeks. Inquire early — Bob'Bae books out.",
  },
];


const TESTIMONIALS = [
  {
    quote:
      "Such an amazing experience! Bob'Bae made everyone feel comfortable and creative, even those of us who had never painted before. The atmosphere was fun, relaxing, and inspiring. I left with a painting I'm actually proud of!",
    name: "First-time painter",
    occasion: "Studio night",
  },
  {
    quote:
      "More than a Paint & Sip — it felt like an art party mixed with therapy and laughter. Bob'Bae's energy is contagious, and his guidance made the process easy and enjoyable. Already booking my next one!",
    name: "Repeat guest",
    occasion: "Private party",
    tag: "Love Black Bob Ross! HA!",
  },
  {
    quote:
      "Absolutely one of the best date nights we've had in Denver. Great music, great vibes, and Joseph is an incredible host and artist. Everyone in the room felt engaged and encouraged.",
    name: "Date-night couple",
    occasion: "Studio session",
  },
];

const PACKAGES = [
  {
    id: "studio",
    label: "Studio",
    price: "$50",
    unit: "/ person",
    cap: "Up to 15 guests",
    summary:
      "At the Joseph Graves Jr. Studio in Denver. Everything is set up — you bring the company.",
    includes: [
      "Canvas, brushes, palette, paints",
      "Easels & aprons",
      "Step-by-step instruction by Bob'Bae",
      "Music & studio vibes",
      "BYOB — wine, beer, snacks welcome",
    ],
  },
  {
    id: "offsite",
    label: "Off-Site",
    price: "$65",
    unit: "/ person",
    cap: "8 – 25 guests · Denver metro",
    summary:
      "Bob'Bae travels to your home, venue, or office. Same full setup — at your place.",
    includes: [
      "All supplies brought to you",
      "Setup & breakdown handled",
      "Step-by-step instruction by Bob'Bae",
      "Travel within Denver metro included",
      "BYOB — host supplies the spread",
    ],
    badge: "Most Booked",
  },
  {
    id: "solo",
    label: "Bob'Bae Solo",
    price: "$400",
    unit: "flat · 2 hrs",
    cap: "Host supplies the materials",
    summary:
      "Just the host, the energy, the instruction. For groups already set up with supplies and a venue.",
    includes: [
      "Bob'Bae Rosé on-site, 2 hours",
      "Step-by-step instruction",
      "Music & character energy",
      "Theme planning before the event",
      "Perfect for small private groups",
    ],
  },
  {
    id: "corporate",
    label: "Corporate",
    price: "$85",
    unit: "/ person",
    cap: "10 – 60 guests · $850 minimum",
    summary:
      "Team-builders without the trust-fall. A two-hour creative reset for your office, off-site, or conference — your team leaves with something they made together.",
    includes: [
      "All supplies, easels, aprons handled",
      "Custom theme tied to your brand or occasion",
      "Setup, breakdown, and cleanup handled",
      "Invoicing · W-9 · COI on request",
      "Optional branded canvases (+$8 each)",
    ],
    badge: "Teams & Off-Sites",
  },
];

const HERO_IMAGES = [
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/8mnl7b8f_IMG_5699.jpg",
    alt: "Bob'Bae Rosé in studio with palette",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/hpi1hlla_IMG_4342.jpg",
    alt: "Bob'Bae Rosé leading a Paint n Sip with mic",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/ngs3ih54_IMG_1757.jpg",
    alt: "Group selfie at a Bob'Bae Paint n Sip event",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/8ehwe1v9_IMG_0776.jpg",
    alt: "Bob'Bae Rosé coaching a young painter",
  },
];

const initial = {
  name: "",
  email: "",
  phone: "",
  package: "studio",
  organization: "",
  preferred_date: "",
  party_size: "",
  theme: "",
  location_details: "",
  special_requests: "",
};

export default function PaintNSip() {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [reelPlaying, setReelPlaying] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");

  const visibleThemes =
    activeCategory === "all"
      ? THEMES
      : THEMES.filter((t) => t.category === activeCategory);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.preferred_date || !form.party_size) {
      toast.error("Please fill out the required fields.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/paint-n-sip`, {
        ...form,
        party_size: Number(form.party_size) || 1,
      });
      setSent(true);
      toast.success("Inquiry sent. Bob'Bae will reply within 48 hours.");
    } catch (err) {
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="paint-n-sip-page">
      {/* HERO */}
      <header className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <img
              src="https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/aj3nxomm_PNG%20Gold%20%281%29.png"
              alt="Bob'Bae Rosé"
              className="h-14 md:h-20 w-auto mb-6 -ml-2"
              data-testid="pns-wordmark"
            />
            <p className="label mb-3">Paint n Sip · Studio Experience</p>
            <h1 className="serif text-5xl md:text-7xl tracking-tight font-light leading-[1.02]">
              Loud laughter,
              <br />
              <span className="italic text-[hsl(var(--ink-soft))]">
                soft canvas.
              </span>
            </h1>
            <p className="mt-8 max-w-xl text-[hsl(var(--ink-soft))] text-lg leading-relaxed">
              Two hours, one canvas, one painting you actually want to hang up.
              Hosted in character by <em>Bob'Bae Rosé</em> — Joseph's alter-ego —
              with music, instruction, and just the right amount of chaos.
            </p>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-[hsl(var(--ink-soft))]">
              <span className="inline-flex items-center gap-2">
                <Clock className="h-4 w-4" /> 2 hours
              </span>
              <span className="inline-flex items-center gap-2">
                <Users className="h-4 w-4" /> 8 – 25 guests
              </span>
              <span className="inline-flex items-center gap-2">
                <MapPin className="h-4 w-4" /> Studio or off-site
              </span>
            </div>
            <a
              href="#book"
              className="mt-10 inline-flex items-center gap-3 bg-foreground text-background px-7 py-3.5 text-xs uppercase tracking-[0.18em] hover:opacity-90"
              data-testid="pns-hero-cta"
            >
              Book your party
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
          <figure className="md:col-span-6 order-1 md:order-2">
            <div className="aspect-[4/5] overflow-hidden bg-[hsl(var(--surface))]">
              <img
                src={HERO_IMAGES[0].src}
                alt={HERO_IMAGES[0].alt}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </figure>
        </div>
      </header>

      {/* MEET BOB'BAE */}
      <section className="border-b border-border" data-testid="pns-meet-bobbae">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <figure className="md:col-span-4 flex justify-center md:justify-start">
            <div className="relative">
              <div
                className="absolute inset-0 -m-3 rounded-full"
                style={{
                  background:
                    "radial-gradient(closest-side, hsl(var(--accent) / 0.15), transparent 75%)",
                }}
                aria-hidden
              />
              <img
                src="https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/7gssbcl1_bob2.png"
                alt="Bob'Bae Rosé — illustrated mascot"
                className="relative w-56 md:w-72 h-auto"
                data-testid="pns-mascot"
              />
            </div>
          </figure>
          <div className="md:col-span-8">
            <p className="label mb-3">Meet the Host</p>
            <h2 className="serif text-3xl md:text-4xl tracking-tight font-light leading-[1.1]">
              I'm Bob'Bae Rosé.
              <br />
              <span className="italic text-[hsl(var(--ink-soft))]">
                And I make the night.
              </span>
            </h2>
            <p className="mt-6 max-w-2xl text-[hsl(var(--ink-soft))] leading-relaxed">
              Joseph Graves Jr.'s alter-ego, born for the room. Dashiki on,
              afro up, palette in hand, mug ever-present. Bob'Bae doesn't teach
              painting — he <em>throws</em> a painting party, and you happen
              to leave with a canvas. Equal parts instructor, hype-man, and
              warm-host energy.
            </p>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="bg-[hsl(var(--surface))] border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <header className="mb-12 md:mb-16 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-7">
              <p className="label mb-3">Four Ways to Book</p>
              <h2 className="serif text-4xl md:text-5xl tracking-tight font-light leading-[1.05]">
                Pick the room.
                <br />
                <span className="italic text-[hsl(var(--ink-soft))]">
                  Bob'Bae brings the rest.
                </span>
              </h2>
            </div>
            <p className="md:col-span-5 text-sm text-[hsl(var(--ink-soft))]">
              All packages are 2 hours · 2-week lead time · evenings and weekends
              preferred · subject to studio availability.
              <br />
              <span className="text-[hsl(var(--accent))]">
                Hosting kids?
              </span>{" "}
              Bob'Bae also runs a separate Kids' Paint n Sip experience —
              mention it in the booking form for pricing.
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-6">
            {PACKAGES.map((pkg) => (
              <article
                key={pkg.id}
                className="bg-background border border-border p-6 md:p-7 flex flex-col"
                data-testid={`pns-package-${pkg.id}`}
              >
                <div className="flex items-center justify-between mb-5 gap-2 min-h-[1.75rem]">
                  <p className="label">{pkg.label}</p>
                  {pkg.badge && (
                    <span className="inline-block bg-[hsl(var(--accent))] text-background px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.15em] shrink-0">
                      {pkg.badge}
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="serif text-4xl md:text-5xl font-light tracking-tight">
                    {pkg.price}
                  </span>
                  <span className="label">{pkg.unit}</span>
                </div>
                <p className="text-sm text-[hsl(var(--ink-soft))] mb-6">
                  {pkg.cap}
                </p>
                <p className="text-[hsl(var(--ink-soft))] leading-relaxed mb-6">
                  {pkg.summary}
                </p>
                <ul className="space-y-2.5 text-sm border-t border-border pt-5 mt-auto">
                  {pkg.includes.map((line) => (
                    <li key={line} className="flex items-start gap-3">
                      <Check className="h-3.5 w-3.5 mt-1 shrink-0 text-[hsl(var(--accent))]" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#book"
                  onClick={() => setForm({ ...form, package: pkg.id })}
                  className="mt-7 inline-flex items-center justify-center gap-2 border border-foreground px-5 py-3 text-[0.7rem] uppercase tracking-[0.18em] hover:bg-foreground hover:text-background transition-colors"
                  data-testid={`pns-package-cta-${pkg.id}`}
                >
                  Book {pkg.label}
                  <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="label">How It Works</p>
            <h2 className="serif text-3xl md:text-4xl mt-3 tracking-tight font-light leading-[1.1]">
              Simple paintings.
              <br />
              <span className="italic text-[hsl(var(--ink-soft))]">
                Memorable nights.
              </span>
            </h2>
          </div>
          <ol className="md:col-span-8 divide-y divide-border border-y border-border">
            {[
              {
                step: "01",
                title: "Pick your theme.",
                body: "Themes are fully personalized — Bob'Bae works with the host to design a simple, approachable painting around your occasion. Birthdays, bachelorettes, team-builders, date nights, or just because.",
              },
              {
                step: "02",
                title: "Lock the date.",
                body: "2 weeks lead time recommended. Evenings and weekends preferred. Studio capacity is 15; off-site we can accommodate 8–25.",
              },
              {
                step: "03",
                title: "Show up. Paint. Sip.",
                body: "Bob'Bae arrives in full character. You bring your guests, your snacks, your favorite bottle. Everyone leaves with a finished canvas and a story.",
              },
            ].map((s) => (
              <li
                key={s.step}
                className="py-7 md:py-8 grid grid-cols-12 gap-4 items-baseline"
              >
                <span className="col-span-2 md:col-span-1 label">{s.step}</span>
                <div className="col-span-10 md:col-span-11">
                  <h3 className="serif text-2xl md:text-3xl tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-[hsl(var(--ink-soft))] leading-relaxed max-w-2xl">
                    {s.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* THEME INSPIRATION */}
      <section
        className="border-b border-border"
        data-testid="pns-themes"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <header className="mb-10 md:mb-14 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-7">
              <p className="label mb-3">Theme Inspiration</p>
              <h2 className="serif text-4xl md:text-5xl tracking-tight font-light leading-[1.05]">
                A few favorites
                <br />
                <span className="italic text-[hsl(var(--ink-soft))]">
                  guests have painted.
                </span>
              </h2>
            </div>
            <p className="md:col-span-5 text-sm text-[hsl(var(--ink-soft))]">
              Every theme is personalized — these are just sparks. Pick one,
              remix it, or send Bob'Bae your own idea in the booking form.
            </p>
          </header>

          <div
            className="mb-10 md:mb-12 flex flex-wrap gap-2"
            data-testid="pns-theme-filters"
          >
            {THEME_CATEGORIES.map((c) => {
              const active = c.id === activeCategory;
              return (
                <button
                  key={c.id}
                  onClick={() => setActiveCategory(c.id)}
                  className={`px-4 py-2 text-[0.7rem] uppercase tracking-[0.18em] border transition-colors ${
                    active
                      ? "bg-foreground text-background border-foreground"
                      : "border-border text-[hsl(var(--ink-soft))] hover:border-foreground hover:text-foreground"
                  }`}
                  data-testid={`pns-theme-filter-${c.id}`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {visibleThemes.map((t, i) => (
              <figure
                key={t.title}
                className="group"
                data-testid={`pns-theme-${i}`}
              >
                <div className="aspect-square overflow-hidden bg-[hsl(var(--surface))]">
                  <img
                    src={t.src}
                    alt={t.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                </div>
                <figcaption className="mt-4">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <p className="label">{t.title}</p>
                    {t.kid && (
                      <span className="inline-block bg-[hsl(var(--accent))] text-background px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.15em]">
                        Kids
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[hsl(var(--ink-soft))] leading-relaxed">
                    {t.note}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* THE VIBE — video + event photos */}
      <section className="border-b border-border bg-foreground text-background" data-testid="pns-vibe">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <header className="mb-12 md:mb-16 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-7">
              <p className="label !text-background/60 mb-3">The Vibe</p>
              <h2 className="serif text-4xl md:text-5xl tracking-tight font-light leading-[1.05]">
                Don't take my word for it.
                <br />
                <span className="italic text-background/70">
                  Watch a real party.
                </span>
              </h2>
            </div>
            <p className="md:col-span-5 text-sm text-background/65">
              From studio nights to corporate suites at the stadium — Bob'Bae
              brings the same energy to every room. Press play.
            </p>
          </header>

          <div className="relative aspect-video overflow-hidden bg-background/10 group">
            {reelPlaying ? (
              <video
                src={REEL_SRC}
                controls
                autoPlay
                playsInline
                className="w-full h-full object-cover"
                data-testid="pns-reel-video"
              />
            ) : (
              <>
                <img
                  src={EVENT_PHOTOS[1].src}
                  alt="Bob'Bae Paint n Sip reel"
                  className="w-full h-full object-cover opacity-90 transition-opacity duration-500 group-hover:opacity-100"
                />
                <button
                  onClick={() => setReelPlaying(true)}
                  className="absolute inset-0 flex items-center justify-center"
                  aria-label="Play Bob'Bae reel"
                  data-testid="pns-reel-play"
                >
                  <span className="flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-background/95 text-foreground transition-transform duration-300 group-hover:scale-105">
                    <Play className="h-7 w-7 md:h-8 md:w-8 ml-1" fill="currentColor" />
                  </span>
                </button>
                <span className="absolute bottom-6 left-6 label !text-background/85">
                  Play Bob'Bae reel
                </span>
              </>
            )}
          </div>

          <div className="mt-6 md:mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {EVENT_PHOTOS.map((img, i) => (
              <div
                key={i}
                className="aspect-square overflow-hidden bg-background/10"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        className="border-b border-border bg-[hsl(var(--surface))]"
        data-testid="pns-testimonials"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <header className="mb-12 md:mb-16 grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-7">
              <p className="label mb-3">Guests, in their words</p>
              <h2 className="serif text-4xl md:text-5xl tracking-tight font-light leading-[1.05]">
                "Black Bob Ross."
                <br />
                <span className="italic text-[hsl(var(--ink-soft))]">
                  — actual review.
                </span>
              </h2>
            </div>
            <p className="md:col-span-5 text-sm text-[hsl(var(--ink-soft))]">
              From first-time painters to repeat regulars — what they've said
              after a night with Bob'Bae.
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={i}
                className="bg-background border border-border p-7 md:p-9 flex flex-col"
                data-testid={`pns-testimonial-${i}`}
              >
                <div
                  className="flex gap-1 mb-6"
                  aria-label="5 out of 5 stars"
                >
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star
                      key={s}
                      className="h-4 w-4 text-[hsl(var(--accent))]"
                      fill="currentColor"
                    />
                  ))}
                </div>
                <blockquote className="serif text-xl md:text-[1.35rem] leading-[1.45] flex-1">
                  <span className="text-[hsl(var(--accent))] mr-1">"</span>
                  {t.quote}
                  <span className="text-[hsl(var(--accent))] ml-1">"</span>
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-border">
                  <p className="label">{t.name}</p>
                  <p className="text-sm text-[hsl(var(--ink-soft))] mt-1">
                    {t.occasion}
                  </p>
                  {t.tag && (
                    <p className="mt-3 text-sm italic text-[hsl(var(--accent))]">
                      "{t.tag}"
                    </p>
                  )}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="border-b border-border"
        data-testid="pns-faq"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-4">
            <p className="label mb-3">Good to Know</p>
            <h2 className="serif text-3xl md:text-5xl tracking-tight font-light leading-[1.05]">
              Before you
              <br />
              <span className="italic text-[hsl(var(--ink-soft))]">
                book.
              </span>
            </h2>
            <p className="mt-6 text-[hsl(var(--ink-soft))] leading-relaxed max-w-md">
              The most common questions, answered straight. Anything else —
              just put it in the booking form and Bob'Bae will reply within
              48 hours.
            </p>
          </div>

          <dl className="md:col-span-8 divide-y divide-border border-y border-border">
            {FAQS.map((f, i) => (
              <div
                key={i}
                className="py-6 md:py-7 grid grid-cols-12 gap-4"
                data-testid={`pns-faq-${i}`}
              >
                <dt className="col-span-12 md:col-span-5 serif text-lg md:text-xl tracking-tight leading-snug">
                  {f.q}
                </dt>
                <dd className="col-span-12 md:col-span-7 text-[hsl(var(--ink-soft))] leading-relaxed">
                  {f.a}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* BOOKING FORM */}
      <section id="book" className="border-b border-border scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="label">Inquire</p>
            <h2 className="serif text-3xl md:text-5xl mt-3 tracking-tight font-light leading-[1.05]">
              Tell Bob'Bae about
              <br />
              <span className="italic text-[hsl(var(--ink-soft))]">
                your event.
              </span>
            </h2>
            <p className="mt-6 text-[hsl(var(--ink-soft))] leading-relaxed">
              Send the details and Bob'Bae will confirm availability and theme
              within 48 hours.
            </p>
          </div>

          <div className="md:col-span-8">
            {sent ? (
              <div data-testid="pns-success" className="border border-border p-10 md:p-14">
                <p className="label mb-4">Thank You</p>
                <h3 className="serif text-3xl md:text-4xl tracking-tight font-light">
                  Inquiry received.
                </h3>
                <p className="mt-4 text-[hsl(var(--ink-soft))] leading-relaxed max-w-xl">
                  Bob'Bae will be in touch within 48 hours to confirm
                  availability and walk through your theme. Keep an eye on your
                  inbox.
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="space-y-6"
                data-testid="pns-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <Field label="Your name *">
                    <input
                      required
                      type="text"
                      className="pns-input"
                      value={form.name}
                      onChange={update("name")}
                      data-testid="pns-name"
                    />
                  </Field>
                  <Field label="Email *">
                    <input
                      required
                      type="email"
                      className="pns-input"
                      value={form.email}
                      onChange={update("email")}
                      data-testid="pns-email"
                    />
                  </Field>
                  <Field label="Phone (optional)">
                    <input
                      type="tel"
                      className="pns-input"
                      value={form.phone}
                      onChange={update("phone")}
                      data-testid="pns-phone"
                    />
                  </Field>
                  <Field label="Package *">
                    <select
                      required
                      className="pns-input"
                      value={form.package}
                      onChange={update("package")}
                      data-testid="pns-package"
                    >
                      <option value="studio">Studio · $50/pp (max 15)</option>
                      <option value="offsite">Off-Site · $65/pp (8–25)</option>
                      <option value="solo">Bob'Bae Solo · $400 flat</option>
                      <option value="corporate">Corporate · $85/pp (10–60)</option>
                      <option value="kids">Kids' Paint n Sip · custom quote</option>
                    </select>
                  </Field>
                  <Field label="Preferred date *">
                    <input
                      required
                      type="date"
                      className="pns-input"
                      value={form.preferred_date}
                      onChange={update("preferred_date")}
                      data-testid="pns-date"
                    />
                  </Field>
                  <Field label="Party size *">
                    <input
                      required
                      type="number"
                      min="1"
                      max="50"
                      className="pns-input"
                      placeholder="e.g., 12"
                      value={form.party_size}
                      onChange={update("party_size")}
                      data-testid="pns-party-size"
                    />
                  </Field>
                </div>

                <Field label="Theme or painting idea">
                  <input
                    type="text"
                    className="pns-input"
                    placeholder="Sunflowers · birthday motif · cowboy silhouette · surprise me…"
                    value={form.theme}
                    onChange={update("theme")}
                    data-testid="pns-theme"
                  />
                </Field>

                {form.package === "corporate" && (
                  <Field label="Company or organization">
                    <input
                      type="text"
                      className="pns-input"
                      placeholder="Acme Inc. · For invoicing and COI"
                      value={form.organization}
                      onChange={update("organization")}
                      data-testid="pns-organization"
                    />
                  </Field>
                )}

                <Field label="Location details (for off-site)">
                  <input
                    type="text"
                    className="pns-input"
                    placeholder="Home address, venue name, or 'TBD'"
                    value={form.location_details}
                    onChange={update("location_details")}
                    data-testid="pns-location"
                  />
                </Field>

                <Field label="Special requests">
                  <textarea
                    rows={4}
                    className="pns-input"
                    placeholder="Occasion, color palette, dietary needs, parking, anything Bob'Bae should know…"
                    value={form.special_requests}
                    onChange={update("special_requests")}
                    data-testid="pns-requests"
                  />
                </Field>

                <button
                  disabled={busy}
                  className="inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90 disabled:opacity-50"
                  data-testid="pns-submit"
                >
                  {busy ? "Sending…" : "Send inquiry"}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
                <p className="text-xs text-[hsl(var(--ink-soft))] max-w-md pt-2">
                  Your inquiry goes straight to the studio. Your details are
                  never shared or added to any list.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* TAKE BOB'BAE HOME — MERCH TEASE */}
      <section
        className="border-b border-border bg-foreground text-background"
        data-testid="pns-merch"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
          <figure className="md:col-span-5 order-2 md:order-1">
            <div className="aspect-square overflow-hidden bg-foreground/40 flex items-center justify-center p-8">
              <img
                src="https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/oe05vg8j_Bobbae%20shirt.png"
                alt="Bob'Bae Rosé t-shirt"
                className="max-w-full max-h-full w-auto h-auto object-contain"
              />
            </div>
          </figure>
          <div className="md:col-span-7 order-1 md:order-2">
            <p className="label !text-background/60 mb-3">Wear the Character</p>
            <h2 className="serif text-3xl md:text-5xl tracking-tight font-light leading-[1.05]">
              Take Bob'Bae
              <br />
              <span className="italic text-background/70">home.</span>
            </h2>
            <p className="mt-6 max-w-xl text-background/75 leading-relaxed">
              The Bob'Bae Rosé tee — the same character that hosts the party,
              now on a shirt you'll actually want to wear. Available alongside
              the rest of the studio merch at Graves Collective.
            </p>
            <a
              href="https://gravescollective.com/apparel"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-3 bg-background text-foreground px-7 py-3.5 text-xs uppercase tracking-[0.18em] hover:opacity-90"
              data-testid="pns-merch-cta"
            >
              Shop Bob'Bae merch
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>

      <style>{`
        .pns-input {
          width: 100%;
          background: transparent;
          border: none;
          border-bottom: 1px solid hsl(var(--border));
          padding: 0.65rem 0;
          font-size: 1rem;
          color: hsl(var(--ink));
          outline: none;
          transition: border-color 0.3s ease;
        }
        .pns-input:focus { border-color: hsl(var(--ink)); }
        textarea.pns-input { border: 1px solid hsl(var(--border)); padding: 1rem; }
        textarea.pns-input:focus { border-color: hsl(var(--ink)); }
        select.pns-input {
          padding-right: 1rem;
          appearance: none;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='%23121212' d='M3 6l5 5 5-5z'/></svg>");
          background-repeat: no-repeat;
          background-position: right 0.25rem center;
          background-size: 14px;
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
